import { NumberField } from "@/components/form/number-field";
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import {
  AI_MODEL_PRESETS,
  AI_PROVIDERS,
  type AiConfigInput,
  type AiFeature,
  useAiConfig,
  useAiFeedbackSummary,
  useAiUsage,
  useTestAiConfig,
  useUpdateAiConfig,
} from "@/api/ai";

export const Route = createFileRoute("/_app/settings/ai")({
  component: AiSettings,
});

interface FeatureMeta {
  key: AiFeature;
  name: string;
  desc: string;
}

const FEATURE_GROUPS: { title: string; note?: string; items: FeatureMeta[] }[] = [
  {
    title: "Authoring help",
    items: [
      {
        key: "draft",
        name: "Pipeline drafting",
        desc: "Generate a pipeline from a plain-English description.",
      },
      {
        key: "fieldMapping",
        name: "Field-mapping suggestions",
        desc: "Match source fields to destination columns.",
      },
      {
        key: "copilots",
        name: "Inline copilots",
        desc: "Cron, Groovy script, PII masking and connector-setup helpers.",
      },
    ],
  },
  {
    title: "Operations",
    items: [
      {
        key: "diagnose",
        name: "Diagnose failed runs",
        desc: "Root-cause a failed or stale run with suggested next steps.",
      },
      {
        key: "edit",
        name: "Natural-language pipeline edit",
        desc: "Change a pipeline's steps by describing the change.",
      },
    ],
  },
  {
    title: "Autonomous",
    note: "Opt-in — these use noticeably more tokens.",
    items: [
      {
        key: "agent",
        name: "Agentic diagnosis",
        desc: "Let diagnosis run a multi-step tool-use investigation.",
      },
      {
        key: "autoDiagnose",
        name: "Auto-diagnose on failure",
        desc: "Diagnose a run automatically the moment it fails.",
      },
      {
        key: "actions",
        name: "AI-applied fixes",
        desc: "Diagnosis proposes concrete fixes (replay, pause, disable step) you apply with one click.",
      },
    ],
  },
];

interface FormState {
  enabled: boolean;
  provider: string;
  model: string;
  apiKey: string;
  baseUrl: string;
  wire: string;
  fastModel: string;
  proxyUrl: string;
  budget: string;
  features: Record<AiFeature, boolean>;
}

const ALL_ON: Record<AiFeature, boolean> = {
  draft: true,
  fieldMapping: true,
  diagnose: true,
  edit: true,
  agent: false,
  autoDiagnose: false,
  copilots: true,
  actions: false,
};

function AiSettings() {
  const toast = useToast();
  const { data, isLoading, error, refetch } = useAiConfig();
  const usage = useAiUsage();
  const feedback = useAiFeedbackSummary();
  const test = useTestAiConfig();
  const update = useUpdateAiConfig();

  const [f, setF] = useState<FormState>({
    enabled: true,
    provider: "anthropic",
    model: "",
    apiKey: "",
    baseUrl: "",
    wire: "",
    fastModel: "",
    proxyUrl: "",
    budget: "",
    features: { ...ALL_ON },
  });
  const [advanced, setAdvanced] = useState(false);
  const [tested, setTested] = useState<{ signature: string; ok: boolean; message?: string } | null>(
    null,
  );

  useEffect(() => {
    if (data) {
      setF({
        enabled: data.enabled ?? true,
        provider: data.provider ?? "anthropic",
        model: data.model ?? "",
        apiKey: "",
        baseUrl: data.baseUrl ?? "",
        wire: data.wire ?? "",
        fastModel: data.fastModel ?? "",
        proxyUrl: data.proxyUrl ?? "",
        budget: data.monthlyTokenBudget != null ? String(data.monthlyTokenBudget) : "",
        features: {
          draft: data.features?.draft ?? true,
          fieldMapping: data.features?.fieldMapping ?? true,
          diagnose: data.features?.diagnose ?? true,
          edit: data.features?.edit ?? true,
          agent: data.features?.agent ?? false,
          autoDiagnose: data.features?.autoDiagnose ?? false,
          copilots: data.features?.copilots ?? true,
          actions: data.features?.actions ?? false,
        },
      });
      setTested(null);
      if (data.proxyUrl || data.baseUrl || data.wire) setAdvanced(true);
    }
  }, [data]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((p) => ({ ...p, [k]: v }));
  const setFeature = (key: AiFeature, on: boolean) =>
    setF((p) => ({ ...p, features: { ...p.features, [key]: on } }));

  const modelOptions = useMemo(
    () => data?.knownModels?.[f.provider] ?? AI_MODEL_PRESETS[f.provider] ?? [],
    [data?.knownModels, f.provider],
  );

  const body = useMemo<AiConfigInput>(
    () => ({
      enabled: f.enabled,
      provider: f.provider,
      model: f.model.trim() || undefined,
      apiKey: f.apiKey.trim() || undefined,
      baseUrl: f.baseUrl.trim() || null,
      wire: f.wire.trim() || null,
      fastModel: f.fastModel.trim() || null,
      proxyUrl: f.proxyUrl.trim() || null,
      monthlyTokenBudget: f.budget.trim() ? Number(f.budget.trim()) : null,
      features: f.features,
    }),
    [f],
  );
  // Only connection settings gate Save behind a passing test — feature toggles,
  // fast model and budget save freely.
  const connSignature = JSON.stringify({
    provider: f.provider,
    model: f.model.trim(),
    apiKey: f.apiKey.trim(),
    baseUrl: f.baseUrl.trim(),
    wire: f.wire.trim(),
    proxyUrl: f.proxyUrl.trim(),
  });
  const savedConnSignature = data
    ? JSON.stringify({
        provider: data.provider ?? "anthropic",
        model: data.model ?? "",
        apiKey: "",
        baseUrl: data.baseUrl ?? "",
        wire: data.wire ?? "",
        proxyUrl: data.proxyUrl ?? "",
      })
    : null;
  const connectionChanged = savedConnSignature != null && connSignature !== savedConnSignature;
  const mustTest = !data?.configured || connectionChanged;
  const connectionVerified = !mustTest || Boolean(tested?.ok && tested.signature === connSignature);

  const keyKnown = data?.apiKeySet && !f.apiKey.trim();
  const canTest = f.provider === "ollama" || Boolean(f.apiKey.trim()) || Boolean(keyKnown);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;

  async function runTest() {
    const res = await test.mutateAsync(body);
    setTested({ signature: connSignature, ok: res.ok, message: res.message });
    if (res.ok) toast.success("Connection OK");
  }

  async function save() {
    await update.mutateAsync(body);
    toast.success("AI configuration saved");
    setF((p) => ({ ...p, apiKey: "" }));
  }

  return (
    <PageContainer>
      <PageHeader
        title="AI configuration"
        description="Connect an LLM provider, then choose which AI features are available in this workspace."
        actions={
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {!connectionVerified ? (
              <Typography variant="caption" color="text.secondary">
                Test the connection to save
              </Typography>
            ) : null}
            <Button
              variant="contained"
              disabled={!connectionVerified || update.isPending}
              onClick={save}
            >
              Save
            </Button>
          </Stack>
        }
      />

      <Stack spacing={3}>
        {/* ── Provider connection ─────────────────────────────────────── */}
        <Section
          title="Provider connection"
          action={
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={f.enabled}
                  onChange={(e) => set("enabled", e.target.checked)}
                />
              }
              label="Enabled"
            />
          }
        >
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1.4fr" },
            }}
          >
            <TextField
              select
              size="small"
              label="Provider"
              value={f.provider}
              onChange={(e) => set("provider", e.target.value)}
            >
              {AI_PROVIDERS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              freeSolo
              size="small"
              options={modelOptions}
              value={f.model}
              onChange={(_, v) => set("model", v ?? "")}
              onInputChange={(_, v) => set("model", v)}
              renderInput={(params) => (
                <TextField {...params} label="Model" placeholder="known model or custom id" />
              )}
            />
            <TextField
              size="small"
              type="password"
              label={
                data?.apiKeySet
                  ? "API key (blank keeps current)"
                  : f.provider === "ollama"
                    ? "API key (not needed for Ollama)"
                    : "API key"
              }
              value={f.apiKey}
              onChange={(e) => set("apiKey", e.target.value)}
            />
          </Box>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={runTest} disabled={!canTest || test.isPending}>
              {test.isPending ? "Testing…" : "Test connection"}
            </Button>
            {data?.source ? (
              <Chip
                size="small"
                variant="outlined"
                label={data.source === "tenant" ? "Saved for this tenant" : "Using env defaults"}
              />
            ) : null}
          </Stack>
          {test.isPending ? <LinearProgress /> : null}
          {tested && tested.signature === connSignature ? (
            <Alert severity={tested.ok ? "success" : "error"}>
              {tested.message ?? (tested.ok ? "Connected" : "Test failed")}
            </Alert>
          ) : tested ? (
            <Alert severity="info">
              Connection settings changed since the last test — test again before saving.
            </Alert>
          ) : null}
        </Section>

        {/* ── Model routing ──────────────────────────────────────────── */}
        <Section
          title="Model routing"
          subtitle="An optional cheaper model for classification-style calls and agent-loop steps. Generation always uses the main model."
        >
          <Autocomplete
            freeSolo
            size="small"
            options={modelOptions}
            value={f.fastModel}
            onChange={(_, v) => set("fastModel", v ?? "")}
            onInputChange={(_, v) => set("fastModel", v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Fast model (optional)"
                placeholder="falls back to the main model"
              />
            )}
          />
        </Section>

        {/* ── Features ───────────────────────────────────────────────── */}
        <Section
          title="Features"
          subtitle="Turn individual AI capabilities on or off for everyone in this workspace."
        >
          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            }}
          >
            {FEATURE_GROUPS.map((group) => (
              <Box key={group.title}>
                <Typography variant="overline" color="text.secondary">
                  {group.title}
                </Typography>
                {group.note ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    {group.note}
                  </Typography>
                ) : null}
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {group.items.map((it) => (
                    <FormControlLabel
                      key={it.key}
                      sx={{ alignItems: "flex-start", m: 0 }}
                      control={
                        <Switch
                          size="small"
                          sx={{ mt: 0.25 }}
                          checked={f.features[it.key]}
                          onChange={(e) => setFeature(it.key, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="body2">{it.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {it.desc}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Section>

        {/* ── Budget ─────────────────────────────────────────────────── */}
        <Section
          title="Monthly token budget"
          subtitle="Estimated tokens (prompt + response) across all AI features. Calls are rejected once the month's usage reaches this. Blank means unlimited."
        >
          <NumberField
            size="small"
            integer
            label="Token budget"
            value={f.budget}
            onValueChange={(value) => set("budget", value == null ? "" : String(value))}
            min={0}
            placeholder="e.g. 2000000"
            sx={{ maxWidth: 260 }}
          />
        </Section>

        {/* ── Network (advanced) ─────────────────────────────────────── */}
        <Section title="Network">
          <Button
            size="small"
            onClick={() => setAdvanced((v) => !v)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: advanced ? "rotate(180deg)" : "none",
                  transition: "transform .15s",
                }}
              />
            }
            sx={{ alignSelf: "flex-start" }}
          >
            Proxy, base URL and wire-format overrides
          </Button>
          <Collapse in={advanced}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                size="small"
                label="Outbound proxy URL"
                value={f.proxyUrl}
                onChange={(e) => set("proxyUrl", e.target.value)}
                placeholder="http://proxy.internal:8080 — routes all LLM calls"
              />
              <TextField
                size="small"
                label="Base URL override"
                value={f.baseUrl}
                onChange={(e) => set("baseUrl", e.target.value)}
                placeholder="https://my-gateway/v1 — Azure, self-host, gateway"
              />
              <TextField
                select
                size="small"
                label="Wire format override"
                value={f.wire}
                onChange={(e) => set("wire", e.target.value)}
                sx={{ maxWidth: 320 }}
              >
                <MenuItem value="">Auto (from provider)</MenuItem>
                <MenuItem value="openai">OpenAI /chat/completions</MenuItem>
                <MenuItem value="anthropic">Anthropic /v1/messages</MenuItem>
              </TextField>
            </Stack>
          </Collapse>
        </Section>

        {/* ── Usage ──────────────────────────────────────────────────── */}
        {usage.data && usage.data.invocations > 0 ? (
          <Section title={`Usage this month (${usage.data.month})`}>
            <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
              <Metric label="Calls" value={usage.data.invocations.toLocaleString()} />
              <Metric label="Est. tokens" value={usage.data.estimatedTokens.toLocaleString()} />
              <Metric
                label="Budget"
                value={usage.data.budget ? usage.data.budget.toLocaleString() : "unlimited"}
              />
              {feedback.data && feedback.data.acceptanceRate != null ? (
                <Metric
                  label="Accepted"
                  value={`${feedback.data.acceptanceRate}% (${feedback.data.up}/${
                    feedback.data.up + feedback.data.down
                  })`}
                />
              ) : null}
            </Stack>
            {usage.data.byFeature.length > 0 ? (
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mt: 1 }}>
                {usage.data.byFeature.map((r) => (
                  <Chip
                    key={r.feature}
                    size="small"
                    variant="outlined"
                    label={`${r.feature}: ${r.calls} · ${r.tokens.toLocaleString()} tok`}
                  />
                ))}
              </Stack>
            ) : null}
          </Section>
        ) : null}
      </Stack>
    </PageContainer>
  );
}

function Section({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          sx={{ alignItems: "flex-start", justifyContent: "space-between", mb: subtitle ? 0.5 : 2 }}
        >
          <Typography variant="subtitle1">{title}</Typography>
          {action}
        </Stack>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        ) : null}
        <Stack spacing={2}>{children}</Stack>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </div>
  );
}
